/**
 * AuthContext — resolves the active user identity + access token + role.
 *
 * On mount:
 *   - Inside Teams host → Teams SSO via `getTeamsAuthToken()`
 *   - Outside Teams    → MSAL `acquireTokenSilent` with redirect fallback
 *
 * After the identity is known we call `apiClient.retail.config.users.byEmail(upn)`
 * to fetch the user's `{ roles, permissions, branchCode, regionCode, hierarchy }`
 * from the v3 SQL-backed UserRoles table.
 *
 * Per teams-app-wiring §4.1:
 *   - `primaryRole` is derived from `roles[]` using a fixed precedence:
 *     SuperAdmin > Executive > Manager > Compliance > Auditor > Supervisor >
 *     FSR > Kiosk > Customer.
 *   - 503 on the user-roles route → `status: 'initializing'` + a restricted
 *     fallback role (`Customer`). A banner is surfaced via `error`.
 *   - 404 on the user-roles route → `status: 'unprovisioned'` + restricted
 *     fallback role; a friendly message is surfaced via `error`.
 *
 * We build our own ApiClient here (via the same `createApiClient` factory the
 * ApiClientContext uses) because AuthContext is a parent of ApiClientProvider
 * and cannot consume `useApiClient()`.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from 'react';
import { ApiError } from '@retail-ops/api-client';
import type { WhoamiResponse } from '@retail-ops/api-client';
import type { UserRole } from '@retail-ops/shared-types/enums';
import type { UserRoleDto, MyContextResponse, TestModeInfo } from '@retail-ops/shared-types';
import { initTeams, getTeamsAuthToken, getTeamsContext } from '@/services/teams';
import { getMsalToken, msalInstance, ensureMsalReady, tryBrowserSsoSilent } from '@/services/msal';
import { createApiClient, apiClientFactoryOptionsFromEnv } from '@/services/apiClient';
import { APP_VERSION } from '@/constants/version';
// Wave 13 — admin-auth-hardening. Flip the prefer-token flag in
// CrudTable's bypass-key prompt whenever we resolve a Teams SSO / MSAL
// bearer token, so editor saves skip the bypass-key prompt.
import { setAdminAuthTokenAvailable } from '@/modules/Admin/editors/_shared/CrudTable';

// ---------------------------------------------------------------------------
// Role precedence — highest-wins when a user has multiple role rows.
// Wave 1 G-7 (Bill 2026-04-27): canonical 8-role schema. SystemOps + Risk
// are panel-only and intentionally rank ABOVE Officer so a panel user who
// somehow lands in ROP-Teams resolves to a non-cart-visible role.
// ---------------------------------------------------------------------------

// Risk and SystemOps are panel roles with no officer-app surfaces (risk-mgmt-panel / sysops-panel), so they
// rank BELOW Officer: an Officer who also holds Risk is still an Officer here (2026-10-07 - ranked above, an
// Officer+Risk tester got an empty module rail).
const ROLE_PRECEDENCE: readonly UserRole[] = [
  'SuperAdmin',
  'Admin',
  'Executive',
  'Manager',
  'Supervisor',
  'Officer',
  'Risk',
  'SystemOps',
];

/**
 * Fallback role used when UserRoles returns 503 (v3 config not seeded), 404
 * (user not provisioned), or the roles array is otherwise empty. 'Officer'
 * is the lowest-privilege ROP-Teams role; an unresolved user gets the most
 * restrictive cart-visibility scope (own-branch unclaimed + own carts) and
 * no admin / Risk / SystemOps surfaces. Consumers that need to distinguish
 * "real unresolved user" from "real Officer" should check `user.status`.
 */
const RESTRICTED_FALLBACK_ROLE: UserRole = 'Officer';

/**
 * Kept as a type-level alias for historical reasons. Always resolves to a
 * known `UserRole` so downstream consumers (ShellLayout, RoleLanding) can
 * index into `Record<UserRole, ...>` maps without widening.
 */
export type PrimaryRole = UserRole;

/**
 * Accepts either a flat `string[]` or the shape the Function currently returns
 * (UserRoleAssignment row objects with a `roleCode` field). Falls through to
 * other common property names (`code`, `role`) before giving up.
 */
function normalizeRoleCodes(
  roles: ReadonlyArray<unknown> | undefined,
): ReadonlyArray<string> {
  if (!roles || roles.length === 0) return [];
  const out: string[] = [];
  for (const r of roles) {
    if (typeof r === 'string') {
      const t = r.trim();
      if (t) out.push(t);
      continue;
    }
    if (r && typeof r === 'object') {
      const obj = r as Record<string, unknown>;
      const code =
        (typeof obj.roleCode === 'string' && obj.roleCode) ||
        (typeof obj.code === 'string' && obj.code) ||
        (typeof obj.role === 'string' && obj.role) ||
        '';
      if (code) out.push(code.trim());
    }
  }
  return out;
}

function pickPrimaryRole(roles: ReadonlyArray<unknown> | undefined): PrimaryRole {
  const codes = normalizeRoleCodes(roles);
  if (codes.length === 0) return RESTRICTED_FALLBACK_ROLE;
  // Stored casing is mixed (Officer / OFFICER / ADMIN) - match case-insensitively, return the canonical name.
  const known = new Set<string>(codes.map((c) => c.toLowerCase()));
  for (const r of ROLE_PRECEDENCE) {
    if (known.has(r.toLowerCase())) return r;
  }
  return RESTRICTED_FALLBACK_ROLE;
}

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

export interface AuthUser {
  upn: string;
  displayName: string;
  tenantId?: string;

  /** All role codes returned by the v3 UserRoles lookup. */
  roles: ReadonlyArray<string>;
  /** Single highest-precedence role — drives nav + landing routing. */
  primaryRole: PrimaryRole;
  /** Effective-permission map keyed by permission code. */
  permissions: Record<string, boolean>;

  /**
   * Active branch — equal to `activeBranchCode` once `/v3/session/my-context`
   * resolves. During the 404/503 fallback (route not deployed) this falls
   * back to whatever `/v3/config/user-roles` returned. Treat this as the
   * branch the officer is "working from right now" — read it for cart/
   * session bootstraps, never the primary.
   */
  branchCode?: string;
  regionCode?: string;
  officerCode?: string;
  officerTeam?: string;
  effectiveLevel?: number;
  isCompliance?: boolean;
  isKiosk?: boolean;
  subordinatesEmails?: ReadonlyArray<string>;
  allDescendantEmails?: ReadonlyArray<string>;
  // nmlsId + appDepartment columns dropped from dbo.Users 2026-05-14
  // (hallucinated; Entra is authoritative for department; NMLS doesn't
  // belong on the user record). Removed from the User type to match.

  // ---------------------------------------------------------------------
  // Branch auto-detect / branch-of-day override (CONFIG-03)
  //   primaryBranchCode = home branch (Entra-sync-derived)
  //   activeBranchCode  = primary OR today's override row
  //   `branchCode` above is an alias of activeBranchCode for backcompat.
  // Falls back to the legacy `branchCode` value with overrideActive=false
  // when the new `/v3/session/my-context` route 404s (not yet deployed).
  // ---------------------------------------------------------------------
  primaryBranchCode?: string;
  primaryBranchName?: string;
  activeBranchCode?: string;
  activeBranchName?: string;
  overrideActive?: boolean;
  overrideSetAt?: string;
  overrideReason?: string;

  /**
   * Provisioning status of the account:
   *   - 'provisioned'  : UserRoles row returned 200 with data
   *   - 'initializing' : UserRoles returned 503 (v3 config ETL not seeded yet)
   *   - 'unprovisioned': UserRoles returned 404 (no row for this email)
   */
  status: 'provisioned' | 'initializing' | 'unprovisioned';

  // ── Session-mode flags (Sprint 1 — Bill 2026-05-08) ──────────────────
  // Per-user toggles read by `useSessionMode`. They surface from the
  // /v3/session/my-context resolver and default to false / undefined when
  // the row is absent.

  /** Highest-precedence role label (alias of `primaryRole` with the
   * SuperAdmin / Admin tier names preserved). */
  permissionRole?: string;
  /** Admin toggle: forces this officer into Sandbox mode for every cart. */
  isInSandboxMode?: boolean;
  /** Admin toggle: enrolls this officer in the UAT pilot cohort. */
  isUATEnrolled?: boolean;
  /** SuperAdmin toggle: grants Test-Live override during Dev Mode. */
  hasTestLiveAccess?: boolean;
  /** Per-officer designated test inbox for simulated notifications. */
  testRecipientEmail?: string;
  /**
   * TEST MODE (Pilot/Test, Bill 2026-10-07): live Core Director, test customers only.
   * Server-resolved (per-user flag, officer list or bank-wide setting) and enforced
   * server-side; the app uses it only for the banner and the person-form help text.
   */
  testMode?: TestModeInfo;
}

export interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isInTeams: boolean;
  loading: boolean;
  error: string | null;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  /**
   * Re-fetch `/v3/session/my-context` and merge the branch slice back into
   * the cached user. Called by `BranchOverrideModal` after a write so the
   * `ActiveBranchChip` updates without a full refresh().
   */
  /** Resolves true when the server returned the branch context and the chip was updated. */
  refreshBranch: () => Promise<boolean>;
  /**
   * REQ-82 keyless-cutover diagnostic. Result of GET /v3/session/whoami at
   * startup — tells us whether the Teams SSO bearer cryptographically validated
   * end-to-end (BEFORE the function key is removed from the bundle). Surfaced
   * visibly via <AuthDiagBadge> so it can be read inside the Teams client where
   * the console is not inspectable. Null until the probe resolves.
   */
  authDiag: WhoamiResponse | null;
  /**
   * REQ-82 keyless-cutover — FRONTEND Teams SSO probe. Captures the result of
   * calling getTeamsAuthToken() DIRECTLY (the error is otherwise swallowed by
   * the api-client getToken catch), plus the Teams host/client + resolved UPN.
   * This is what tells us WHY `hasBearer:false` — i.e. why Teams isn't issuing
   * an SSO token. Null until the probe resolves.
   */
  ssoProbe: SsoProbe | null;
}

/** Frontend Teams SSO diagnostic (REQ-82). */
export interface SsoProbe {
  inTeams: boolean;
  /**
   * The actual iframe origin the tab loaded from (window.location.origin).
   * DECISIVE for the SSO resource/origin mismatch: if this is the bootstrap
   * SWA host (red-wave-*.azurestaticapps.net) the Teams client is still on the
   * CACHED old manifest; if it's https://retail.mypcb.com the new manifest is
   * active and SSO should match the api://retail.mypcb.com/<id> resource.
   */
  origin?: string;
  /** Teams host clientType: 'desktop' | 'web' | 'android' | 'ios' | undefined. */
  clientType?: string;
  /** Teams-context UPN — what byEmail() uses for role resolution. */
  upn?: string;
  /** True if getTeamsAuthToken() returned a non-empty token. */
  ssoOk: boolean;
  /** Length of the acquired token (no value echoed). */
  ssoTokenLen?: number;
  /** The actual getTeamsAuthToken() failure message (normally swallowed). */
  ssoError?: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isInTeams, setIsInTeams] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // REQ-82 keyless-cutover startup probe result (GET /v3/session/whoami).
  const [authDiag, setAuthDiag] = useState<WhoamiResponse | null>(null);
  // REQ-82 keyless-cutover — frontend Teams SSO probe (why hasBearer:false).
  const [ssoProbe, setSsoProbe] = useState<SsoProbe | null>(null);

  // Hold onto the latest identity so refresh() can re-run role resolution.
  const identityRef = useRef<{
    upn: string;
    displayName: string;
    tenantId?: string;
    inTeams: boolean;
  } | null>(null);

  const resolveIdentity = async (): Promise<{
    upn: string;
    displayName: string;
    tenantId?: string;
    inTeams: boolean;
    token: string | null;
  }> => {
    const inTeams = await initTeams();
    setIsInTeams(inTeams);

    if (inTeams) {
      const ctx = getTeamsContext();
      const upn = ctx?.user?.userPrincipalName ?? 'unknown@mypcb.com';
      const displayName = ctx?.user?.displayName ?? upn;
      const tenantId = ctx?.user?.tenant?.id;

      // Block Teams-web short of attempting SSO. Teams web triggers an MSAL
      // redirect flow against a URI not registered in the AAD app, so the
      // user hits AADSTS50011. Per Bill 2026-04-23, web Teams is not a
      // supported surface — users should use Teams desktop or mobile.
      const clientType = ctx?.app?.host?.clientType;
      if (clientType === 'web') {
        throw new Error('TEAMS_WEB_NOT_SUPPORTED');
      }

      // Teams SSO bearer-token acquisition. Will fail with "App resource
      // defined in manifest and iframe origin do not match" until the Entra
      // app has an App ID URI registered for the current iframe host
      // (e.g., api://thankful-glacier-...azurestaticapps.net/<clientId>).
      // That's a Ray/Entra-admin change. Until it lands, we fall back to
      // context-only auth — /v3/* routes accept x-functions-key without a
      // Bearer token, so the app still works.
      let token: string | null = null;
      try {
        token = await getTeamsAuthToken();
      } catch (tokErr) {
        // eslint-disable-next-line no-console
        console.warn(
          '[auth] Teams SSO bearer token unavailable — falling back to ' +
          'context-only auth (x-functions-key). Cause:',
          tokErr instanceof Error ? tokErr.message : String(tokErr),
        );
      }

      return {
        upn,
        displayName,
        ...(tenantId !== undefined ? { tenantId } : {}),
        inTeams: true,
        token,
      };
    }

    // Browser-direct (not hosted in Teams). Per Solution Bible §3.3 the
    // production auth path is Teams SSO — FSRs never reach this app via a plain
    // browser URL. `VITE_ALLOW_BROWSER_DEV=true` is a LOCAL-DEV-ONLY escape
    // hatch that runs the full app in a browser via an interactive popup; it
    // MUST NOT be set in the production build (.env.production.local).
    const allowBrowserDev =
      (import.meta.env.VITE_ALLOW_BROWSER_DEV as string | undefined) === 'true';

    if (!allowBrowserDev) {
      // Production browser landing (Bill 2026-06-03). Probe SILENTLY for an
      // existing Microsoft session (iframe, NO popup → no COOP/Edge-148 issue,
      // and no Entra login page that would reveal the app to a visitor):
      //   authenticated → 'USE_TEAMS_APP'   (App.tsx → "open in Teams" page)
      //   not authed     → 'NOT_AUTHORIZED'  (App.tsx → generic gate, no app info)
      const authed = await tryBrowserSsoSilent();
      throw new Error(authed ? 'USE_TEAMS_APP' : 'NOT_AUTHORIZED');
    }

    await ensureMsalReady();
    const t = await getMsalToken();
    const accounts = msalInstance.getAllAccounts();
    const first = accounts[0];
    const upn = first?.username ?? 'dev.user@mypcb.com';
    const displayName = first?.name ?? 'Dev User';
    const tenantId = first?.tenantId;
    return {
      upn,
      displayName,
      ...(tenantId !== undefined ? { tenantId } : {}),
      inTeams: false,
      token: t,
    };
  };

  const resolveRole = async (
    upn: string,
    inTeams: boolean,
  ): Promise<{
    roles: ReadonlyArray<string>;
    primaryRole: PrimaryRole;
    permissions: Record<string, boolean>;
    branchCode?: string;
    regionCode?: string;
    officerCode?: string;
    officerTeam?: string;
    effectiveLevel?: number;
    isCompliance?: boolean;
    isKiosk?: boolean;
    subordinatesEmails?: ReadonlyArray<string>;
    allDescendantEmails?: ReadonlyArray<string>;
    // nmlsId + appDepartment dropped 2026-05-14 (see User type above).
    status: AuthUser['status'];
    warning: string | null;
  }> => {
    const client = createApiClient(apiClientFactoryOptionsFromEnv(inTeams));
    try {
      const envelope = await client.retail.config.users.byEmail(upn);
      if (envelope.success && envelope.data) {
        // Function returns: { user: {...}, roles: [{roleCode, ...}], permissions: [], hierarchy: [], entraMirror }
        // Older/alternate shape (UserRoleDto): flat fields + roles: string[] + permissions: Record<string,bool>
        // Accept both — read from whichever path has the value.
        const raw = envelope.data as unknown as Record<string, unknown>;
        const userObj = (raw['user'] && typeof raw['user'] === 'object')
          ? (raw['user'] as Record<string, unknown>)
          : raw;
        const pick = (key: string): string | undefined => {
          const v = userObj[key];
          return typeof v === 'string' && v.length > 0 ? v : undefined;
        };
        const pickBool = (key: string): boolean | undefined => {
          const v = userObj[key];
          return typeof v === 'boolean' ? v : undefined;
        };
        const pickNum = (key: string): number | undefined => {
          const v = userObj[key];
          return typeof v === 'number' ? v : undefined;
        };
        const rolesRaw = Array.isArray(raw['roles']) ? (raw['roles'] as unknown[]) : [];
        const roleCodes = normalizeRoleCodes(rolesRaw);

        // permissions: Function returns array of UserPermission rows OR object.
        // Coerce to Record<string, boolean>.
        const permissionsRaw = raw['permissions'];
        let permissions: Record<string, boolean> = {};
        if (Array.isArray(permissionsRaw)) {
          for (const p of permissionsRaw) {
            if (p && typeof p === 'object') {
              const po = p as Record<string, unknown>;
              const code = (typeof po['permissionCode'] === 'string' && po['permissionCode'])
                || (typeof po['code'] === 'string' && po['code'])
                || '';
              const granted = typeof po['isGranted'] === 'boolean' ? po['isGranted'] : true;
              if (code) permissions[code] = granted;
            }
          }
        } else if (permissionsRaw && typeof permissionsRaw === 'object') {
          permissions = permissionsRaw as Record<string, boolean>;
        }

        // hierarchy: array of { subordinateEmail } rows OR flat subordinatesEmails array.
        const hierarchyRaw = raw['hierarchy'];
        let subordinatesEmails: ReadonlyArray<string> | undefined;
        if (Array.isArray(hierarchyRaw)) {
          const emails = hierarchyRaw
            .map((h) => (h && typeof h === 'object'
              ? ((h as Record<string, unknown>)['subordinateEmail']
                 ?? (h as Record<string, unknown>)['email'])
              : undefined))
            .filter((e): e is string => typeof e === 'string' && e.length > 0);
          if (emails.length > 0) subordinatesEmails = emails;
        }

        const out: {
          roles: ReadonlyArray<string>;
          primaryRole: PrimaryRole;
          permissions: Record<string, boolean>;
          branchCode?: string;
          regionCode?: string;
          officerCode?: string;
          officerTeam?: string;
          effectiveLevel?: number;
          isCompliance?: boolean;
          isKiosk?: boolean;
          subordinatesEmails?: ReadonlyArray<string>;
          allDescendantEmails?: ReadonlyArray<string>;
          // nmlsId + appDepartment dropped 2026-05-14.
          status: AuthUser['status'];
          warning: string | null;
        } = {
          roles: roleCodes,
          primaryRole: pickPrimaryRole(roleCodes),
          permissions,
          status: 'provisioned',
          warning: null,
        };
        const branchCode    = pick('branchCode');
        const regionCode    = pick('regionCode');
        const officerCode   = pick('officerCode');
        const officerTeam   = pick('officerTeam');
        // nmlsId + appDepartment fields dropped 2026-05-14.
        const effectiveLevel = pickNum('effectiveLevel');
        const isCompliance  = pickBool('isCompliance');
        const isKiosk       = pickBool('isKiosk');
        if (branchCode !== undefined)    out.branchCode = branchCode;
        if (regionCode !== undefined)    out.regionCode = regionCode;
        if (officerCode !== undefined)   out.officerCode = officerCode;
        if (officerTeam !== undefined)   out.officerTeam = officerTeam;
        if (effectiveLevel !== undefined) out.effectiveLevel = effectiveLevel;
        if (isCompliance !== undefined)   out.isCompliance = isCompliance;
        if (isKiosk !== undefined)        out.isKiosk = isKiosk;
        if (subordinatesEmails !== undefined) out.subordinatesEmails = subordinatesEmails;
        return out;
      }
      return {
        roles: [],
        primaryRole: RESTRICTED_FALLBACK_ROLE,
        permissions: {},
        status: 'unprovisioned',
        warning: envelope.error ?? 'User not provisioned in UserRoles.',
      };
    } catch (e) {
      if (ApiError.isApiError(e)) {
        if (e.httpStatus === 503) {
          return {
            roles: [],
            primaryRole: RESTRICTED_FALLBACK_ROLE,
            permissions: {},
            status: 'initializing',
            warning:
              'User role configuration is initializing. Some modules will be unavailable until seeding completes.',
          };
        }
        if (e.httpStatus === 404) {
          return {
            roles: [],
            primaryRole: RESTRICTED_FALLBACK_ROLE,
            permissions: {},
            status: 'unprovisioned',
            warning:
              'Your account is not provisioned for PCB Retail. Please contact your supervisor.',
          };
        }
      }
      const msg = e instanceof Error ? e.message : String(e);
      return {
        roles: [],
        primaryRole: RESTRICTED_FALLBACK_ROLE,
        permissions: {},
        status: 'unprovisioned',
        warning: `Could not resolve user role: ${msg}`,
      };
    }
  };

  /**
   * Layer 2 of the auth bootstrap: call `/v3/session/my-context` to resolve
   * the *active* branch (= primary OR today's override). Returns `null` if
   * the route isn't deployed yet (404) or v3 config is still seeding (503);
   * the caller falls back to the `branchCode` from `/v3/config/user-roles`.
   *
   * NOTE: this is layered on the existing user-roles call rather than
   * replacing it (per design §6.4 + §5.1 — graceful "route not deployed"
   * fallback so frontend can ship ahead of backend).
   */
  const resolveBranch = async (
    inTeams: boolean,
  ): Promise<{
    primaryBranchCode?: string;
    primaryBranchName?: string;
    activeBranchCode?: string;
    activeBranchName?: string;
    overrideActive?: boolean;
    overrideSetAt?: string;
    overrideReason?: string;
    isInSandboxMode?: boolean;
    testRecipientEmail?: string;
    testMode?: TestModeInfo;
  } | null> => {
    const client = createApiClient(apiClientFactoryOptionsFromEnv(inTeams));
    try {
      const env: MyContextResponse = await client.retail.session.myContext();
      if (!env.success || !env.branch) return null;
      const b = env.branch;
      const out: {
        primaryBranchCode?: string;
        primaryBranchName?: string;
        activeBranchCode?: string;
        activeBranchName?: string;
        overrideActive?: boolean;
        overrideSetAt?: string;
        overrideReason?: string;
        isInSandboxMode?: boolean;
        testRecipientEmail?: string;
        testMode?: TestModeInfo;
      } = {};
      // W6 2026-10-07: training (Sandbox) flag → ModeBanner via useSessionMode.
      if (typeof env.user?.isInSandboxMode === 'boolean') out.isInSandboxMode = env.user.isInSandboxMode;
      if (env.user?.testRecipientEmail) out.testRecipientEmail = env.user.testRecipientEmail;
      // Test mode (Bill 2026-10-07) → Test-mode banner + person-form help.
      if (env.user?.testMode) out.testMode = env.user.testMode;
      if (b.primaryBranchCode) out.primaryBranchCode = b.primaryBranchCode;
      if (b.primaryBranchName) out.primaryBranchName = b.primaryBranchName;
      if (b.activeBranchCode) out.activeBranchCode = b.activeBranchCode;
      if (b.activeBranchName) out.activeBranchName = b.activeBranchName;
      if (typeof b.overrideActive === 'boolean') out.overrideActive = b.overrideActive;
      if (b.overrideSetAt) out.overrideSetAt = b.overrideSetAt;
      if (b.overrideReason) out.overrideReason = b.overrideReason;
      return out;
    } catch (e) {
      // Per design §6.4: 404/503 means the route isn't deployed yet — fall
      // back silently to the legacy `branchCode` from `user-roles`. Other
      // errors also degrade silently (no blocking, no toast); the chip
      // simply stays in "primary only / no override" mode.
      if (ApiError.isApiError(e)) {
        if (e.httpStatus === 404 || e.httpStatus === 503) {
          // eslint-disable-next-line no-console
          console.info(
            '[auth] /v3/session/my-context not yet deployed (HTTP %s) — using primary branch from user-roles.',
            e.httpStatus,
          );
          return null;
        }
      }
      // eslint-disable-next-line no-console
      console.warn(
        '[auth] /v3/session/my-context failed — falling back to user-roles branch:',
        e instanceof Error ? e.message : String(e),
      );
      return null;
    }
  };

  /**
   * REQ-82 keyless-cutover probe. Calls GET /v3/session/whoami through the same
   * api-client used everywhere else (so it carries the Teams SSO bearer AND the
   * function key) and records whether the bearer cryptographically validated.
   * Fire-and-forget: never throws, never blocks sign-in. Surfaced via
   * <AuthDiagBadge> so the verdict is readable inside the Teams client.
   */
  const resolveAuthDiag = async (inTeams: boolean): Promise<void> => {
    try {
      const client = createApiClient(apiClientFactoryOptionsFromEnv(inTeams));
      const res = await client.retail.session.whoami();
      setAuthDiag(res);
    } catch (e) {
      // Surface the failure as a synthetic diag so the badge still shows something.
      setAuthDiag({
        success: false,
        authMethod: 'none',
        hasBearer: false,
        bearerValid: false,
        bearerError: e instanceof Error ? e.message : String(e),
        keyPresent: false,
        officerPresent: false,
      });
    }
  };

  /**
   * REQ-82 keyless-cutover — FRONTEND Teams SSO probe. Calls getTeamsAuthToken()
   * directly and captures the result/error (the api-client getToken catch
   * normally swallows it), plus the Teams host clientType + resolved UPN. This
   * is what reveals WHY the backend sees `hasBearer:false`. Never throws.
   */
  const resolveSsoProbe = async (inTeams: boolean): Promise<void> => {
    const probe: SsoProbe = { inTeams, ssoOk: false };
    try {
      // The iframe origin the tab actually loaded from — tells us whether the
      // Teams client picked up the new manifest (retail.mypcb.com) or is still
      // on the cached old one (red-wave-*.azurestaticapps.net).
      if (typeof window !== 'undefined') probe.origin = window.location.origin;
    } catch { /* origin unavailable */ }
    try {
      const ctx = getTeamsContext();
      const ct = ctx?.app?.host?.clientType;
      const upn = ctx?.user?.userPrincipalName;
      if (ct) probe.clientType = ct;
      if (upn) probe.upn = upn;
    } catch { /* context unavailable — leave undefined */ }
    if (inTeams) {
      try {
        const t = await getTeamsAuthToken();
        probe.ssoOk = Boolean(t);
        if (t) probe.ssoTokenLen = t.length;
      } catch (e) {
        probe.ssoOk = false;
        probe.ssoError = e instanceof Error ? e.message : String(e);
      }
    }
    setSsoProbe(probe);
    // REQ-82 — POST the probe so the cutover is verifiable server-side (App
    // Insights) without transcribing the badge. Fire-and-forget; never throws.
    try {
      const client = createApiClient(apiClientFactoryOptionsFromEnv(inTeams));
      await client.retail.session.logSsoProbe({ ...probe, appVersion: APP_VERSION });
    } catch { /* diagnostic POST is best-effort */ }
  };

  const bootstrap = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const id = await resolveIdentity();
      // REQ-82 keyless-cutover — fire the diagnostics in the background so the
      // auth badge populates without delaying role resolution / sign-in.
      void resolveAuthDiag(id.inTeams);
      void resolveSsoProbe(id.inTeams);
      setToken(id.token);
      // Wave 13 — flip the admin-auth flag ONLY when an actual bearer token
      // resolved. Setting it true on UPN-presence alone (Bill's first attempt)
      // caused 403s when MSAL/Teams SSO returned no token: prompt skipped,
      // bypass-key not attached, request went out un-authed, server rejected.
      // The fallback in promptBypassKey now uses the stored bypass-key
      // automatically when token is absent — no prompt, no error.
      setAdminAuthTokenAvailable(Boolean(id.token));
      identityRef.current = {
        upn: id.upn,
        displayName: id.displayName,
        ...(id.tenantId !== undefined ? { tenantId: id.tenantId } : {}),
        inTeams: id.inTeams,
      };

      const role = await resolveRole(id.upn, id.inTeams);

      // Layer 2 — branch context (CONFIG-03). Falls back to null when
      // /v3/session/my-context is not yet deployed; the legacy
      // role.branchCode is used as both primary and active in that case.
      const branchCtx = await resolveBranch(id.inTeams);

      const nextUser: AuthUser = {
        upn: id.upn,
        displayName: id.displayName,
        ...(id.tenantId !== undefined ? { tenantId: id.tenantId } : {}),
        roles: role.roles,
        primaryRole: role.primaryRole,
        permissions: role.permissions,
        status: role.status,
      };
      if (role.regionCode !== undefined) nextUser.regionCode = role.regionCode;
      if (role.officerCode !== undefined) nextUser.officerCode = role.officerCode;
      if (role.officerTeam !== undefined) nextUser.officerTeam = role.officerTeam;
      if (role.effectiveLevel !== undefined) nextUser.effectiveLevel = role.effectiveLevel;
      if (role.isCompliance !== undefined) nextUser.isCompliance = role.isCompliance;
      if (role.isKiosk !== undefined) nextUser.isKiosk = role.isKiosk;
      if (role.subordinatesEmails !== undefined)
        nextUser.subordinatesEmails = role.subordinatesEmails;
      if (role.allDescendantEmails !== undefined)
        nextUser.allDescendantEmails = role.allDescendantEmails;
      // nmlsId + appDepartment dropped 2026-05-14.

      // ---- Branch resolution (with graceful fallback) -----------------
      // When /v3/session/my-context returns data, prefer it (it knows about
      // today's override). Otherwise mirror the legacy `branchCode` into
      // both primary + active so downstream callers always see something.
      if (branchCtx) {
        if (branchCtx.primaryBranchCode) nextUser.primaryBranchCode = branchCtx.primaryBranchCode;
        if (branchCtx.primaryBranchName) nextUser.primaryBranchName = branchCtx.primaryBranchName;
        if (branchCtx.activeBranchCode) {
          nextUser.activeBranchCode = branchCtx.activeBranchCode;
          // Maintain `branchCode` as alias of activeBranchCode (design §5.1).
          nextUser.branchCode = branchCtx.activeBranchCode;
        }
        if (branchCtx.activeBranchName) nextUser.activeBranchName = branchCtx.activeBranchName;
        nextUser.overrideActive = branchCtx.overrideActive ?? false;
        if (branchCtx.overrideSetAt) nextUser.overrideSetAt = branchCtx.overrideSetAt;
        if (branchCtx.overrideReason) nextUser.overrideReason = branchCtx.overrideReason;
        if (branchCtx.isInSandboxMode !== undefined) nextUser.isInSandboxMode = branchCtx.isInSandboxMode;
        if (branchCtx.testRecipientEmail) nextUser.testRecipientEmail = branchCtx.testRecipientEmail;
        if (branchCtx.testMode) nextUser.testMode = branchCtx.testMode;
      } else {
        // Fallback path — route not deployed yet. Use user-roles branchCode
        // as both primary and active; mark override inactive.
        if (role.branchCode !== undefined) {
          nextUser.branchCode = role.branchCode;
          nextUser.primaryBranchCode = role.branchCode;
          nextUser.activeBranchCode = role.branchCode;
        }
        nextUser.overrideActive = false;
      }

      setUser(nextUser);

      if (role.warning) setError(role.warning);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown auth error';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Lightweight refetch — only the branch slice. Called by
   * BranchOverrideModal after a successful POST/DELETE so the chip
   * updates without re-running role resolution.
   */
  const refreshBranch = async (): Promise<boolean> => {
    const id = identityRef.current;
    if (!id) return false;
    const branchCtx = await resolveBranch(id.inTeams);
    if (!branchCtx) return false;
    setUser((prev) => {
      if (!prev) return prev;
      const next: AuthUser = { ...prev };
      if (branchCtx.primaryBranchCode) next.primaryBranchCode = branchCtx.primaryBranchCode;
      if (branchCtx.primaryBranchName) next.primaryBranchName = branchCtx.primaryBranchName;
      if (branchCtx.activeBranchCode) {
        next.activeBranchCode = branchCtx.activeBranchCode;
        next.branchCode = branchCtx.activeBranchCode;
      }
      if (branchCtx.activeBranchName) next.activeBranchName = branchCtx.activeBranchName;
      next.overrideActive = branchCtx.overrideActive ?? false;
      if (branchCtx.overrideSetAt) {
        next.overrideSetAt = branchCtx.overrideSetAt;
      } else {
        delete next.overrideSetAt;
      }
      if (branchCtx.overrideReason) {
        next.overrideReason = branchCtx.overrideReason;
      } else {
        delete next.overrideReason;
      }
      return next;
    });
    return true;
  };

  const logout = async (): Promise<void> => {
    if (!isInTeams) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts[0]) {
        await msalInstance.logoutRedirect({ account: accounts[0] });
      }
    }
    setToken(null);
    setUser(null);
    setAdminAuthTokenAvailable(false);
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isInTeams,
      loading,
      error,
      logout,
      refresh: bootstrap,
      refreshBranch,
      authDiag,
      ssoProbe,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, token, isInTeams, loading, error, authDiag, ssoProbe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
